"use client";

import { useMediaPlayer, useMediaState } from "@vidstack/react";
import { useEffect } from "react";

import { useDataContext } from "@/app/_context";
import type {
  PublicGatedPreview,
  PublicWatermark,
  WatermarkPosition,
} from "@/lib/public-investigation";
import { formatTimecode } from "@/lib/timecode";
import { cn } from "@/lib/utils";

// The desktop app draws these over its own player rather than burning them into
// the video, so the published stream has neither and the web player draws them
// the same way. Render inside <MediaPlayer> so they stay up in fullscreen.

// Bottom positions sit above the control bar; in the widget, top positions
// sit below its title bar.
const POSITION_CLASSES: Record<WatermarkPosition, string> = {
  "top-right": "right-3 text-right",
  "top-left": "left-3",
  "bottom-right": "bottom-14 right-3 text-right",
  "bottom-left": "bottom-14 left-3",
  center:
    "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-12 text-center md:text-sm",
};
const TOP_POSITIONS = new Set<WatermarkPosition>(["top-right", "top-left"]);

function Watermark({
  watermark,
  recordingTimeMs,
}: {
  watermark: PublicWatermark;
  recordingTimeMs: (playerMs: number) => number;
}) {
  const { isPortal } = useDataContext();
  const currentTime = useMediaState("currentTime");
  const parts = [
    watermark.text,
    watermark.outletBadge,
    watermark.showTimestamp
      ? formatTimecode(recordingTimeMs(currentTime * 1000))
      : "",
  ].filter(Boolean);

  return (
    <div
      style={{ opacity: watermark.opacity }}
      className={cn(
        "pointer-events-none absolute z-[5] max-w-[70%] select-none truncate text-xs font-semibold text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.8)]",
        POSITION_CLASSES[watermark.position],
        TOP_POSITIONS.has(watermark.position) &&
          (isPortal ? "top-10 lg:top-14" : "top-3")
      )}
    >
      {parts.join(" · ")}
    </div>
  );
}

// A teaser, not access control: the HLS stream is public, so anyone can still
// watch the whole recording by seeking back, reloading or fetching the stream.
// It shows the journalist's sign-up message once playback passes the free
// preview length, as the desktop player does, and pauses so the recording
// doesn't keep playing behind it.
function GatedPreview({ gatedPreview }: { gatedPreview: PublicGatedPreview }) {
  const { setCurrentPlaybackTime } = useDataContext();
  const player = useMediaPlayer();
  const currentTime = useMediaState("currentTime");
  const paused = useMediaState("paused");
  const isPastPreview = currentTime > gatedPreview.freePreviewSeconds;

  useEffect(() => {
    if (!isPastPreview || paused || !player) return;
    // Bring the app's clock up to the player's first: on pause, VmPlayer
    // writes that clock back into the player, and just after a seek it can
    // still hold the old position.
    setCurrentPlaybackTime(player.currentTime);
    player.pause()?.catch(() => {});
  }, [isPastPreview, paused, player, setCurrentPlaybackTime]);

  if (!isPastPreview) return null;

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 overflow-hidden bg-zinc-950/95 p-4 text-center text-white md:gap-3 md:p-6">
      <p className="text-sm font-semibold md:text-lg">
        {gatedPreview.teaserTitle || "Preview ended"}
      </p>
      {gatedPreview.teaserMessage && (
        <p className="line-clamp-3 max-w-md whitespace-pre-line text-xs text-white/80 md:text-sm">
          {gatedPreview.teaserMessage}
        </p>
      )}
      {gatedPreview.signupUrl && (
        <a
          href={gatedPreview.signupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 rounded-md bg-accent px-4 py-2 text-xs font-semibold text-white hover:opacity-90 md:text-sm"
        >
          {gatedPreview.ctaButtonText || "Sign up"}
        </a>
      )}
    </div>
  );
}

export default function PlayerOverlays({
  watermark,
  gatedPreview,
  recordingTimeMs = (playerMs) => playerMs,
}: {
  watermark: PublicWatermark | null;
  gatedPreview: PublicGatedPreview | null;
  /** Maps the player's clock to the session's full recording. */
  recordingTimeMs?: (playerMs: number) => number;
}) {
  const { isBusinessCardPlaying } = useDataContext();
  if (isBusinessCardPlaying) return null;

  return (
    <>
      {watermark && (
        <Watermark watermark={watermark} recordingTimeMs={recordingTimeMs} />
      )}
      {gatedPreview && <GatedPreview gatedPreview={gatedPreview} />}
    </>
  );
}
